import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {Engine} from '../models/engine/engine.model';
import {EngineCategory} from '../models/engine/engineCategory.model';
import {EngineAttribute} from '../models/engine/engineAttribute.model';
import {JobEngine} from '../models/engine/jobEngine.model';
import {EngineService} from '../services/engine.service';
import {Engines} from '../actions/engine.actions';
import ClearState = Auth.ClearState;
import GetEngine = Engines.GetEngine;
import GetEngines = Engines.GetEngines;
import CreateEngine = Engines.CreateEngine;
import UpdateEngine = Engines.UpdateEngine;
import DeleteEngine = Engines.DeleteEngine;
import GetJobEngine = Engines.GetJobEngine;
import UpdateJobEngine = Engines.UpdateJobEngine;
import SelectJobEngine = Engines.SelectJobEngine;

export interface EngineStateModel {
  engines: Array<Engine>;
  currentEngine: Engine;
  categories: Array<EngineCategory>;
  attributes: Array<EngineAttribute>;
  jobEngine: JobEngine;
}

@State<EngineStateModel>({
  name: 'engine',
  defaults: {
    engines: [],
    currentEngine: null,
    categories: [],
    attributes: [],
    jobEngine: null,
  }
})
@Injectable()
export class EngineState {
  constructor(private engineService: EngineService) {
  }

  @Selector()
  static engines(state: EngineStateModel): Array<Engine> {
    return state.engines;
  }

  @Selector()
  static engine(state: EngineStateModel): Engine {
    return state.currentEngine;
  }

  @Selector()
  static engineCategories(state: EngineStateModel): Array<EngineCategory> {
    return state.categories;
  }

  @Selector()
  static engineAttributes(state: EngineStateModel): Array<EngineAttribute> {
    return state.attributes;
  }

  @Selector()
  static jobEngine(state: EngineStateModel): JobEngine {
    return state.jobEngine;
  }

  @Action(GetEngines)
  getEngines(ctx: StateContext<EngineStateModel>, action: GetEngines): Observable<Engine[]> {
    if (!action.force) {
      const engines = ctx.getState().engines;
      if (engines.length > 0) {
        return of(engines);
      }
    }
    return this.engineService.getEngines().pipe(
      tap(res => {
        ctx.patchState({
          engines: res.engines,
          categories: res.categories,
          attributes: res.attributes
        });
      }),
      map(res => res.engines)
    );
  }

  @Action(GetEngine)
  getEngine(ctx: StateContext<EngineStateModel>, action: GetEngine): Observable<Engine> {
    if (!action.force) {
      const engine = ctx.getState().currentEngine;
      if (engine && engine.id === action.id) {
        return of(engine);
      }

      const cachedEngine = ctx.getState().engines.find(p => p.id === action.id);
      if (cachedEngine) {
        ctx.patchState({
          currentEngine: cachedEngine
        });
        return of(cachedEngine);
      }
    }
    return this.engineService.getEngine(action.id).pipe(
      tap(engine => {
        ctx.patchState({
          currentEngine: engine
        });
      })
    );
  }

  @Action(CreateEngine)
  createEngine(ctx: StateContext<EngineStateModel>, action: CreateEngine): Observable<Engine> {
    return this.engineService.createEngine(action.engine).pipe(
      tap(engine => {
        ctx.patchState({
          currentEngine: engine
        });
      })
    );
  }

  @Action(UpdateEngine)
  updateEngine(ctx: StateContext<EngineStateModel>, action: UpdateEngine): Observable<Engine> {
    return this.engineService.updateEngine(action.engine).pipe(
      tap(engine => {
        ctx.patchState({
          currentEngine: engine
        });
      })
    );
  }

  @Action(DeleteEngine)
  deleteEngine(ctx: StateContext<EngineStateModel>, action: DeleteEngine): Observable<Engine> {
    const engines = ctx.getState().engines;
    const index = engines.indexOf(action.engine);
    return this.engineService.deleteEngine(action.engine.id).pipe(
      tap(engine => {
        ctx.patchState({
          currentEngine: null,
          engines: [
            ...engines.slice(0, index),
            ...index === engines.length - 1 ? [] : engines.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetJobEngine)
  getJobEngine(ctx: StateContext<EngineStateModel>, action: GetJobEngine): Observable<JobEngine> {
    return this.engineService.getJobEngine(action.id).pipe(
      tap(res => {
        ctx.patchState({
          jobEngine: res.jobEngine
        });
      }),
      map(res => res.jobEngine)
    );
  }

  @Action(UpdateJobEngine)
  updateJobEngine(ctx: StateContext<EngineStateModel>, action: UpdateJobEngine): Observable<JobEngine> {
    return this.engineService.updateJobEngine(action.jobEngine).pipe(
      tap(res => {
        ctx.patchState({
          jobEngine: res.jobEngine
        });
      }),
      map(res => res.jobEngine)
    );
  }

  @Action(SelectJobEngine)
  selectJobEngine(ctx: StateContext<EngineStateModel>, action: SelectJobEngine): Observable<JobEngine> {
    const newJobEngine = {
      ...ctx.getState().jobEngine,
      engine: action.engine,
      engineId: action.engine.id
    };
    ctx.patchState({
      jobEngine: newJobEngine
    });
    return of(newJobEngine);
  }

  @Action(ClearState)
  clearState(ctx: StateContext<EngineStateModel>): void {
    ctx.setState({
      engines: [],
      currentEngine: null,
      categories: [],
      attributes: [],
      jobEngine: null,
    });
  }
}
